import { type Dispatch, type RefObject, type SetStateAction, type SyntheticEvent, useEffect, useState } from 'react'
import { GetComments } from '../services/CommentService'
import { CommentParentType, type CommentModel } from '../models/Comment'
import { GetAnnualPlanTeamByPlanOrganizationTaskId } from '../services/AnnualPlanService'
import { type UserPerson } from '../models/UserPerson'
import { toast } from 'react-toastify'
import type { AuthUser } from './use-auth'
import { handleApiResponse } from '../utils/apiHelpers'

interface CommentType {
  comments: CommentModel[]
  setComments: Dispatch<SetStateAction<CommentModel[]>>
  loading: boolean
}

export const useComments = (parentId: number, parentType: CommentParentType): CommentType => {
  const [loading, setLoading] = useState<boolean>(true)
  const [comments, setComments] = useState<CommentModel[]>([])

  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      const { success, errors, response: comments } = await GetComments(parentId, parentType)
      if (success) {
        setComments(Array.isArray(comments) ? comments : [])
      } else {
        for (const error of errors) {
          toast.error(error)
        }
      }
      setLoading(false)
    }

    void fetchData()
  }, [parentId, parentType])

  const sortedComments = comments.sort((a, b) => new Date(b.dateStamp).getTime() - new Date(a.dateStamp).getTime())

  return { comments: sortedComments, setComments, loading }
}

interface TaggingType {
  loading: boolean
  suggestions: UserPerson[]
  tagging: boolean
  handleSuggestionClick: (user: UserPerson) => void
  handleInput: (e: SyntheticEvent) => void
}

export const useTagging = (
  parentId: number,
  parentType: CommentParentType,
  contentEditableRef: RefObject<HTMLDivElement>,
  setContent: Dispatch<SetStateAction<string>>
): TaggingType => {
  const [loading, setLoading] = useState<boolean>(false)
  const [tagQuery, setTagQuery] = useState<string>('')
  const [teamMembers, setTeamMembers] = useState<UserPerson[]>([])
  const [suggestions, setSuggestions] = useState<UserPerson[]>([])
  const [tagging, setTagging] = useState(false)

  const filterUsers = (): UserPerson[] => {
    return teamMembers.filter(user => `${user.firstName}${user.lastName}`.trim()
      .toLowerCase().includes(tagQuery.toLowerCase()))
  }

  const fetchTeam = async (): Promise<void> => {
    setLoading(true)
    switch (parentType) {
      case CommentParentType.PlanOrganizationTask: {
        try {
          const teamMembers = await handleApiResponse(GetAnnualPlanTeamByPlanOrganizationTaskId(parentId))
          if (teamMembers != null) setTeamMembers(teamMembers)
        } catch (e) {
          toast.error('An unknown error occurred while fetching annual plan team members.')
          console.error(e)
        }
        break
      }
      case CommentParentType.Comment:
        break
    }
    setLoading(false)
  }

  useEffect(() => {
    void fetchTeam()
  }, [parentId, parentType])

  useEffect(() => {
    if (tagging) {
      setSuggestions(filterUsers())
    }
  }, [tagging, tagQuery])

  const handleSuggestionClick = (user: UserPerson): void => {
    const selection = window.getSelection()
    const range = selection?.getRangeAt(0)
    if (contentEditableRef.current != null && range != null) {
      // Calculate start of tag query
      const tagStart = range.startOffset - tagQuery.length - 1 // -1 includes the "@"

      // Create a new range to cover the entire tag query
      const tagRange = document.createRange()
      if (tagStart > -1) {
        tagRange.setStart(range.startContainer, tagStart)
        tagRange.setEnd(range.endContainer, range.endOffset)
      }

      // Delete the entire tag query
      tagRange.deleteContents()

      // create span element for the tag color and user id
      const tagSpan = document.createElement('span')
      tagSpan.textContent = `@${user.firstName}${user.lastName}`
      tagSpan.style.color = 'var(--blue-300)'
      tagSpan.dataset.userEmail = user.email

      // Insert the tag span
      range.insertNode(tagSpan)

      // Insert a placeholder before the tag span
      const placeholderBefore = document.createTextNode('\u200B') // Zero-width space
      tagSpan.parentNode?.insertBefore(placeholderBefore, tagSpan)

      // Insert a placeholder after the tag span
      const placeholderAfter = document.createTextNode('\u200B') // Zero-width space
      tagSpan.parentNode?.appendChild(placeholderAfter)

      // Move the caret to right after the placeholder
      range.setStartAfter(placeholderAfter)

      range.collapse(true)
      selection?.removeAllRanges()
      selection?.addRange(range)

      setTagging(false)
      setSuggestions([])

      const newText = contentEditableRef.current.innerHTML ?? ''
      setContent(newText)
    }
  }

  const handleInput = (e: SyntheticEvent): void => {
    e.preventDefault()
    const value = e.currentTarget.innerHTML ?? ''
    setContent(value)

    // Get the current selected text and caret position
    const selection = window.getSelection()
    const range = selection?.getRangeAt(0)
    const caretPosition = range?.startOffset ?? 0
    const containerNode = range?.startContainer // Might be the full div, or a text node inside it
    const containerText = containerNode?.textContent ?? ''

    // Get the tag element and its next sibling if it exists (in both node form and element form)
    const tagSpan = containerNode?.parentElement
    const nextNodeInTree = tagSpan?.nextSibling
    const nextElementInTree = tagSpan?.nextElementSibling

    // Remove the existing tag span if we are currently editing one and inject the tag text back into the container
    const tagExists = tagSpan?.hasAttribute('data-user-email')
    if (tagExists === true) {
      let textContent = tagSpan?.textContent ?? ''

      // Merge with the next node in the tree if it is also a tag
      // (needed when deleting the last part of one tag and the first part of another)
      if (nextNodeInTree != null && nextElementInTree != null &&
        nextNodeInTree.isSameNode(nextElementInTree) && nextElementInTree.hasAttribute('data-user-email')
      ) {
        textContent += nextElementInTree.textContent ?? ''
        nextElementInTree.remove()
      }

      tagSpan?.remove()
      const textNode = document.createTextNode(textContent)
      range?.insertNode(textNode)
      // set the caret position to the same position as before but in the new text node
      range?.setStart(textNode, caretPosition)
      range?.collapse(true)
    }

    // If the caret is at the end of the current text block and the next node is a tag, remove it.
    // This is needed when deleting the @ sign of a tag or the placeholder before a tag
    const nextNode = containerNode?.nextSibling
    const nextElement = nextNode?.firstChild?.parentElement // The next node in element form so we can query it
    if (nextNode != null && nextElement != null &&
      nextNode.isSameNode(nextElement) && nextElement.hasAttribute('data-user-email') &&
      caretPosition === containerText.length
    ) {
      const textContent = nextNode?.textContent ?? ''
      nextNode?.replaceWith(textContent)
    }

    // Get the last occurrence of "@" before the caret position
    const containerTextBefore = containerText.substring(0, caretPosition)
    const lastAtIndex = containerTextBefore.lastIndexOf('@')

    if (lastAtIndex !== -1) {
      const query = containerText.substring(lastAtIndex + 1, caretPosition)

      // If we typed anything but a space or punctuation, or just removed the tag, display the suggestions
      if (!/\s|[.,!?;]/.test(query) || tagExists === true) {
        setTagging(true)
        setTagQuery(query)
      } else {
        setTagging(false)
        setTagQuery('')
        setSuggestions([])
      }
    } else {
      setTagging(false)
      setTagQuery('')
      setSuggestions([])
    }
  }

  return { loading, suggestions, tagging, handleSuggestionClick, handleInput }
}

export const extractTaggedUserEmails = (text: string): string[] => {
  const userEmails: string[] = []
  const tempDiv = document.createElement('div') // create temp div to query
  tempDiv.innerHTML = text

  const tagSpans = tempDiv.querySelectorAll('span[data-user-email]')

  tagSpans.forEach(span => {
    const userEmail = (span.getAttribute('data-user-email') ?? '').trim()
    if (userEmail !== '') {
      userEmails.push(userEmail)
    }
  })

  return Array.from(new Set(userEmails)) // remove duplicates
}

export const createCommentModel = (
  commentId: number,
  user: AuthUser,
  content: string,
  isResolved: boolean,
  parentId: number,
  parentType: CommentParentType
): CommentModel => {
  return {
    id: commentId,
    userId: user.id,
    commentText: content,
    dateStamp: new Date().toISOString().replace('Z', ''),
    isResolved,
    parentId,
    parentType,
    user: {
      id: user.id,
      firstName: user.firstName,
      lastName: user.lastName,
      imageKey: user.imageKey
    }
  }
}
